// All requests are same-origin: nginx (or the Vite dev server) proxies /api and /ws to
// the backend's internal ALB. The frontend never learns a backend hostname, which is why
// there is no CORS configuration anywhere in this project.

import type {
  Citation,
  DocumentSummary,
  IngestionJob,
  KnowledgeStatus,
} from "../types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = typeof body.detail === "string" ? body.detail : detail;
    } catch {
      /* response had no JSON body; the status line is the best message available */
    }
    throw new Error(detail);
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  createSession: () => request<{ session_id: string }>("/api/sessions", { method: "POST" }),

  knowledgeStatus: () => request<KnowledgeStatus>("/api/knowledge/status"),

  listDocuments: () => request<DocumentSummary[]>("/api/knowledge/documents"),

  readDocument: (key: string) =>
    request<{ key: string; content: string }>(
      `/api/knowledge/documents/${encodeURIComponent(key)}`,
    ),

  writeDocument: (key: string, content: string) =>
    request<DocumentSummary>(`/api/knowledge/documents/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),

  deleteDocument: (key: string) =>
    request<void>(`/api/knowledge/documents/${encodeURIComponent(key)}`, { method: "DELETE" }),

  startSync: () => request<IngestionJob>("/api/knowledge/sync", { method: "POST" }),

  syncStatus: (jobId: string) =>
    request<IngestionJob>(`/api/knowledge/sync/${encodeURIComponent(jobId)}`),

  search: (q: string) =>
    request<Citation[]>(`/api/knowledge/search?q=${encodeURIComponent(q)}`),
};
